import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ResourcesService, ResourceItem, DailyInsightMessage, Research } from "../api";

const BACKEND_FETCH_SIZE = 30; // How many items to fetch from backend at once
const FRONTEND_PAGE_SIZE = 15; // How many items to show per page
const MAX_CACHE_SIZE = 60; // Maximum items to keep in cache

const ResourcesPage: React.FC = () => {
  const [allResources, setAllResources] = useState<ResourceItem[]>([]); // Cache of all fetched resources
  const [currentPage, setCurrentPage] = useState(0); // 0-based page index
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMoreData, setHasMoreData] = useState(true); // Whether there might be more data to fetch
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [lastFetchReturnedFull, setLastFetchReturnedFull] = useState(true); // Track if last fetch returned full results

  const navigate = useNavigate();
  const location = useLocation();
  const resultsTopRef = useRef<HTMLDivElement>(null);

  // Initialize page from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const pageParam = urlParams.get('page');
    if (pageParam) {
      const pageNum = parseInt(pageParam, 10) - 1;
      if (!isNaN(pageNum) && pageNum >= 0) {
        setCurrentPage(pageNum);
      }
    }
    fetchInitialResources();
  }, []);

  const updatePageInUrl = (page: number) => {
    const urlParams = new URLSearchParams(location.search);
    if (page === 0) {
      urlParams.delete('page');
    } else {
      urlParams.set('page', (page + 1).toString());
    }

    const newSearch = urlParams.toString();
    const newPath = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
    navigate(newPath, { replace: false });
  };

  const scrollToTop = () => {
    if (resultsTopRef.current) {
      resultsTopRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const fetchInitialResources = async () => {
    setLoading(true);
    try {
      const data = await ResourcesService.resourcesList(BACKEND_FETCH_SIZE);
      setAllResources(data);
      setHasMoreData(data.length === BACKEND_FETCH_SIZE);
      setLastFetchReturnedFull(data.length === BACKEND_FETCH_SIZE);
      setError(null);
      setIsInitialLoad(false);
    } catch (err) {
      console.error("Error fetching resources:", err);
      setError("Failed to load your resources. Please try again later.");
      setIsInitialLoad(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreResources = async () => {
    if (!hasMoreData || loading || allResources.length === 0) return;

    setLoading(true);
    try {
      const oldestItem = allResources[allResources.length - 1];
      const beforeCursor = oldestItem.sort_date || oldestItem.created;

      const newData = await ResourcesService.resourcesList(BACKEND_FETCH_SIZE, beforeCursor);

      if (newData.length > 0) {
        const existingIds = new Set(allResources.map(item => `${item.resource_type}-${item.id}`));
        const uniqueNewData = newData.filter(item =>
          !existingIds.has(`${item.resource_type}-${item.id}`)
        );

        const combinedResources = [...allResources, ...uniqueNewData];
        combinedResources.sort((a, b) => new Date(b.sort_date!).getTime() - new Date(a.sort_date!).getTime());

        const trimmedResources = combinedResources.slice(0, MAX_CACHE_SIZE);

        setAllResources(trimmedResources);

        const gotFullBatch = newData.length === BACKEND_FETCH_SIZE;
        setHasMoreData(gotFullBatch);
        setLastFetchReturnedFull(gotFullBatch);
      } else {
        setHasMoreData(false);
        setLastFetchReturnedFull(false);
      }
    } catch (err) {
      console.error("Error fetching more resources:", err);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPageResources = () => {
    const startIndex = currentPage * FRONTEND_PAGE_SIZE;
    const endIndex = startIndex + FRONTEND_PAGE_SIZE;
    return allResources.slice(startIndex, endIndex);
  };

  const checkAndFetchMoreData = async () => {
    const totalNeeded = (currentPage + 1) * FRONTEND_PAGE_SIZE;

    if (allResources.length < totalNeeded && hasMoreData) {
      await fetchMoreResources();
    }
  };

  const handleNextPage = () => {
    const maxPage = Math.ceil(allResources.length / FRONTEND_PAGE_SIZE) - 1;
    if (currentPage < maxPage || hasMoreData) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      updatePageInUrl(newPage);

      setTimeout(scrollToTop, 100);
      setTimeout(checkAndFetchMoreData, 0);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      updatePageInUrl(newPage);

      setTimeout(scrollToTop, 100);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const truncateText = (text: string, maxLength = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const renderResourceItem = (resource: ResourceItem) => {
    const isDailyMessage = resource.resource_type === 'daily_message';
    const dailyMessage = resource as DailyInsightMessage;
    const research = resource as Research;

    // Capitalize first letter of research query
    const capitalizeFirstLetter = (str: string) => {
      return str.charAt(0).toUpperCase() + str.slice(1);
    };

    return (
      <div key={`${resource.resource_type}-${resource.id}`} className="resource-item">
        <div className="resource-item__header">
          <div className="resource-item__type">
            {isDailyMessage ? "📅 Daily Message" : "🌐 Web Research"}
          </div>
          <div className="resource-item__date">
            {isDailyMessage
              ? formatDate(dailyMessage.delivery_date)
              : formatDate(research.created)
            }
          </div>
        </div>

        <div className="resource-item__content">
          {isDailyMessage ? (
            <>
              <h3 className="resource-item__title">
                {formatDate(dailyMessage.delivery_date)}
              </h3>
              <p className="resource-item__preview">
                {truncateText(dailyMessage.message_text)}
              </p>
            </>
          ) : (
            <>
              <h3 className="resource-item__title">
                🔍 {capitalizeFirstLetter(research.query)}
              </h3>
            </>
          )}
        </div>

        <div className="resource-item__actions">
          <Link
            to={isDailyMessage
              ? `/resources/daily-message/${resource.id}`
              : `/resources/research/${resource.id}`
            }
            className="button is-outlined"
          >
            View {isDailyMessage ? "Message" : "Report"}
          </Link>
        </div>
      </div>
    );
  };

  const renderPagination = () => {
    const currentPageResources = getCurrentPageResources();
    const totalCachedPages = Math.ceil(allResources.length / FRONTEND_PAGE_SIZE);

    // Determine if we can go to next page
    const canGoNext = currentPage < totalCachedPages - 1 || (hasMoreData && lastFetchReturnedFull);
    const canGoPrevious = currentPage > 0;

    if (!canGoNext && !canGoPrevious) {
      return null;
    }

    return (
      <div className="pagination-container mt-3">
        <div className="pagination">
          <button
            className="pagination__button"
            disabled={!canGoPrevious || loading}
            onClick={handlePreviousPage}
          >
            Previous
          </button>

          <span className="pagination__info">
            {currentPageResources.length} items
            {loading && " • Loading..."}
          </span>

          <button
            className="pagination__button"
            disabled={!canGoNext || loading}
            onClick={handleNextPage}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  const currentPageResources = getCurrentPageResources();

  return (
    <div className="scientia-container">
      <div className="resources-header">
        <h1 className="title">Your Resources</h1>
        <div ref={resultsTopRef}></div>
        <p className="subtitle">
          View your daily messages and web research reports.
        </p>
      </div>

      {error && (
        <div className="form-message is-error" role="alert">
          <button className="close-button" onClick={() => setError(null)}>×</button>
          {error}
        </div>
      )}


      {isInitialLoad && loading ? (
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="scientia-card">
          <div className="scientia-card__content">
            {allResources.length === 0 ? (
              <div className="empty-state">
                <h3>No resources found</h3>
                <p>
                  You haven't received any daily messages or created any web research reports yet.
                  Start chatting with Scientia to generate some resources!
                </p>
                <Link to="/chat" className="button is-primary">
                  Start Chatting
                </Link>
              </div>
            ) : (
              <>
                <div className="resources-list">
                  {currentPageResources.map(renderResourceItem)}
                </div>
                {renderPagination()}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourcesPage;
